import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { habitService, Habit, UpdateHabitData } from '../../services/habitService';
import './HabitEdit.css';

interface HabitEditProps {
  user: User | null;
  onToggleSidebar: () => void;
}

const HabitEdit: React.FC<HabitEditProps> = ({ user, onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [habit, setHabit] = useState<Habit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  const [editForm, setEditForm] = useState({
    name: '',
    icon: '',
    category: '',
    frequency_type: 'daily' as 'daily' | 'every_x_days' | 'weekly' | 'monthly' | 'custom_interval',
    frequency_value: 1,
    frequency_unit: 'days' as 'days' | 'times_per_week' | 'times_per_month',
    description: '',
    is_active: true
  });

  const categories = [
    { value: 'health', label: 'Health' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'education', label: 'Education' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'social', label: 'Social' },
    { value: 'hobbies', label: 'Hobbies' },
    { value: 'self_care', label: 'Self Care' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'sleep', label: 'Sleep' },
    { value: 'other', label: 'Other' }
  ];

  const commonIcons = ['✅', '💊', '🧘', '📚', '📖', '🚶', '🏃', '💪', '🥗', '💧', '😴', '🎯'];

  useEffect(() => {
    if (id && user?.id) {
      loadHabitData();
    }
  }, [id, user]);

  const loadHabitData = async () => {
    if (!id || !user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const habitId = parseInt(id);
      const habitResponse = await habitService.getHabitById(habitId);
      
      if (habitResponse.success && habitResponse.habit) {
        setHabit(habitResponse.habit);
        setEditForm({
          name: habitResponse.habit.name,
          icon: habitResponse.habit.icon,
          category: habitResponse.habit.category,
          frequency_type: habitResponse.habit.frequency_type as 'daily' | 'every_x_days' | 'weekly' | 'monthly' | 'custom_interval',
          frequency_value: habitResponse.habit.frequency_value,
          frequency_unit: habitResponse.habit.frequency_unit as 'days' | 'times_per_week' | 'times_per_month',
          description: habitResponse.habit.description || '',
          is_active: habitResponse.habit.is_active
        });
      } else {
        setError(habitResponse.message || 'Failed to load habit');
      }
    } catch (err) {
      console.error('Error loading habit data:', err);
      setError('Failed to load habit data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!habit || !editForm.name.trim()) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const updateData: UpdateHabitData = {
        name: editForm.name.trim(),
        icon: editForm.icon,
        category: editForm.category,
        frequency_type: editForm.frequency_type,
        frequency_value: editForm.frequency_value,
        frequency_unit: editForm.frequency_unit,
        description: editForm.description.trim() || undefined,
        is_active: editForm.is_active
      };
      
      const response = await habitService.updateHabit(habit.id, updateData);
      
      if (response.success) {
        // Navigate back to habit detail page
        navigate(`/habits/${habit.id}`);
      } else {
        setError(response.message || 'Failed to update habit');
      }
    } catch (err) {
      console.error('Error updating habit:', err);
      setError('Failed to update habit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    navigate(`/habits/${id}`);
  };

  const handleDeleteHabit = async () => {
    if (!habit || !window.confirm('Are you sure you want to delete this habit? This action cannot be undone.')) return;
    
    try {
      setSaving(true);
      const response = await habitService.deleteHabit(habit.id);
      
      if (response.success) {
        navigate('/habits');
      } else {
        setError(response.message || 'Failed to delete habit');
      }
    } catch (err) {
      console.error('Error deleting habit:', err);
      setError('Failed to delete habit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getFrequencyInputLimits = (frequencyType: string) => {
    switch (frequencyType) {
      case 'daily':
        return { min: 1, max: 1 };
      case 'every_x_days':
        return { min: 1, max: 365 };
      case 'weekly':
        return { min: 1, max: 7 };
      case 'monthly':
        return { min: 1, max: 31 };
      case 'custom_interval':
        return { min: 1, max: 365 };
      default:
        return { min: 1, max: 100 };
    }
  };

  if (loading) {
    return (
      <div className="habit-edit">
        <header className="habit-edit-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate(`/habits/${id}`)}>
              ←
            </button>
          </div>
          <h1>/habit-edit</h1>
        </header>
        <div className="habit-edit-content">
          <div className="loading-state">
            <p>Loading habit details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !habit) {
    return (
      <div className="habit-edit">
        <header className="habit-edit-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate(`/habits/${id}`)}>
              ←
            </button>
          </div>
          <h1>/habit-edit</h1>
        </header>
        <div className="habit-edit-content">
          <div className="error-state">
            <p>{error || 'Habit not found'}</p>
            <button onClick={() => navigate('/habits')} className="back-to-habits-btn">
              Back to Habits
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="habit-edit">
      <header className="habit-edit-header">
        <div className="header-left">
          <button className="back-btn" onClick={handleCancelEdit}>
            ←
          </button>
        </div>
        <h1>/habit-edit</h1>
        <div className="header-right">
          <button 
            className="cancel-btn" 
            onClick={handleCancelEdit}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="save-btn" 
            onClick={handleSaveEdit}
            disabled={saving || !editForm.name.trim()}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button 
            className="delete-btn" 
            onClick={handleDeleteHabit}
            disabled={saving}
          >
            Delete
          </button>
        </div>
      </header>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="habit-edit-content">
        {/* Habit Header */}
        <div className="habit-edit-header-section">
          <div className="habit-edit-icon-large">{editForm.icon}</div>
          <div className="habit-edit-info">
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              className="habit-name-input"
              placeholder="Habit name"
              autoFocus
            />
            <div className="habit-edit-meta">
              <span className="habit-edit-category">{categories.find(c => c.value === editForm.category)?.label}</span>
              <span className="habit-edit-frequency">
                {editForm.frequency_type === 'daily' && 'Every day'}
                {editForm.frequency_type === 'every_x_days' && `Every ${editForm.frequency_value} day${editForm.frequency_value > 1 ? 's' : ''}`}
                {editForm.frequency_type === 'weekly' && `${editForm.frequency_value} time${editForm.frequency_value > 1 ? 's' : ''} per week`}
                {editForm.frequency_type === 'monthly' && `${editForm.frequency_value} time${editForm.frequency_value > 1 ? 's' : ''} per month`}
                {editForm.frequency_type === 'custom_interval' && `${editForm.frequency_value} time${editForm.frequency_value > 1 ? 's' : ''} in ${editForm.frequency_value} day${editForm.frequency_value > 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="edit-form">
          <h3>Habit Settings</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Icon</label>
              <div className="icon-selector">
                {commonIcons.map((icon) => (
                  <button
                    key={icon}
                    className={`icon-option ${editForm.icon === icon ? 'selected' : ''}`}
                    onClick={() => setEditForm({ ...editForm, icon })}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Frequency</label>
              <div className="frequency-inputs">
                <select
                  value={editForm.frequency_type}
                  onChange={(e) => {
                    const newType = e.target.value as 'daily' | 'every_x_days' | 'weekly' | 'monthly' | 'custom_interval';
                    const limits = getFrequencyInputLimits(newType);
                    setEditForm({ 
                      ...editForm, 
                      frequency_type: newType,
                      frequency_value: Math.min(Math.max(editForm.frequency_value, limits.min), limits.max)
                    });
                  }}
                >
                  <option value="daily">Every day</option>
                  <option value="every_x_days">Every X days</option>
                  <option value="weekly">X times per week</option>
                  <option value="monthly">X times per month</option>
                  <option value="custom_interval">X times in Y days</option>
                </select>
                {editForm.frequency_type !== 'daily' && (
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={editForm.frequency_value}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/^0+/, '') || '0';
                      const numValue = parseInt(rawValue, 10) || 0;
                      const limits = getFrequencyInputLimits(editForm.frequency_type);
                      const clampedValue = Math.min(Math.max(numValue, limits.min), limits.max);
                      setEditForm({ ...editForm, frequency_value: clampedValue });
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="Value"
                  />
                )}
              </div>
              <div className="frequency-hint">
                {editForm.frequency_type === 'daily' && 'This habit will be tracked every day'}
                {editForm.frequency_type === 'every_x_days' && `This habit will be tracked every ${editForm.frequency_value} day${editForm.frequency_value > 1 ? 's' : ''}`}
                {editForm.frequency_type === 'weekly' && `This habit will be tracked ${editForm.frequency_value} time${editForm.frequency_value > 1 ? 's' : ''} per week`}
                {editForm.frequency_type === 'monthly' && `This habit will be tracked ${editForm.frequency_value} time${editForm.frequency_value > 1 ? 's' : ''} per month`}
                {editForm.frequency_type === 'custom_interval' && `This habit will be tracked ${editForm.frequency_value} time${editForm.frequency_value > 1 ? 's' : ''} within ${editForm.frequency_value} day${editForm.frequency_value > 1 ? 's' : ''}`}
              </div>
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <div className="status-toggle">
                <label className="status-option">
                  <input
                    type="radio"
                    name="status"
                    checked={editForm.is_active}
                    onChange={() => setEditForm({ ...editForm, is_active: true })}
                  />
                  <span className="status-label active">Active</span>
                </label>
                <label className="status-option">
                  <input
                    type="radio"
                    name="status"
                    checked={!editForm.is_active}
                    onChange={() => setEditForm({ ...editForm, is_active: false })}
                  />
                  <span className="status-label inactive">Inactive</span>
                </label>
              </div>
              <div className="status-hint">
                {editForm.is_active 
                  ? 'This habit will be shown in tracking views and can be completed'
                  : 'This habit will be hidden from tracking views but data will be preserved'
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HabitEdit;
